import { useCallback } from 'react';
import useStickyState from './useStickyState';

const STORAGE_KEY = 'reports.positionMode';
// 'all_shorts' was retired (the with_shorts report folder is no longer
// generated). The validator in useStickyState drops any persisted value not
// in this list, so users who had 'all_shorts' selected gracefully fall back
// to 'long_only' on next render — no stuck-on-dead-mode bug.
const VALID_MODES = ['long_only', 'rec_shorts'];

/**
 * Returns whether the current admin user is allowed to see/use the Positions
 * filter (long-only vs with-shorts vs with-rec-shorts). Today this is gated
 * to a single admin (`apalshah`); the backend enforces the same rule
 * independently, so this is just for UI affordance.
 */
export const canUseShorts = () => (localStorage.getItem('adminUsername') || '') === 'apalshah';

/**
 * Shared hook for the Positions mode (long_only | all_shorts | rec_shorts).
 *
 * - Persists across navigation/refresh via localStorage.
 * - For users without shorts permission, the value is locked to 'long_only'
 *   and the setter is a no-op — pages using this hook will behave exactly
 *   as if the toggle didn't exist.
 *
 * Use the returned `mode` as the `position_mode` query param when calling
 * any report endpoint.
 */
export default function usePositionMode() {
  // Validator drops persisted values that are no longer in the option set —
  // e.g. if we ever rename or remove one of the modes, existing localStorage
  // entries fall back to long_only instead of being stuck on a dead key.
  const [storedMode, setStoredMode] = useStickyState(
    STORAGE_KEY,
    'long_only',
    (v) => VALID_MODES.includes(v)
  );
  const allowed = canUseShorts();

  const mode = allowed && VALID_MODES.includes(storedMode) ? storedMode : 'long_only';

  const setMode = useCallback((next) => {
    if (!allowed) return;
    if (VALID_MODES.includes(next)) setStoredMode(next);
  }, [allowed, setStoredMode]);

  return { mode, setMode, canUseShorts: allowed, isShortsActive: mode !== 'long_only' };
}
